/**
 * Auth gate (Phase 5b, step 6).
 *
 * Wraps the editor. Behaviour depends on persistence mode:
 *   - Local (no Supabase env vars): completely transparent. Renders children
 *     immediately — the editor loads exactly as it did before Phase 5b, and
 *     none of the auth code below even runs. This keeps the default/tested
 *     path unchanged.
 *   - Cloud (Supabase configured): intercepts. While the session is being
 *     resolved, a splash; with no session, the magic-link sign-in screen;
 *     with a session, the editor.
 *
 * Gating at this level also means `App` (and its `hydrate()` effect) doesn't
 * mount until there's an authenticated user — so the seed-on-empty-library
 * flow never tries to write before RLS has a `auth.uid()` to match.
 *
 * The cloud-only hooks live in `CloudAuthGate`, which is only mounted when
 * cloud persistence is active, so there's no conditional-hook concern.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { isCloudPersistence } from '../persistence';
import { getCurrentSession, onAuthChange, sendMagicLink } from '../persistence/supabaseClient';

export function AuthGate({ children }: { children: ReactNode }) {
  if (!isCloudPersistence()) return <>{children}</>;
  return <CloudAuthGate>{children}</CloudAuthGate>;
}

type AuthStatus = 'loading' | 'signedOut' | 'signedIn';

function CloudAuthGate({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');

  useEffect(() => {
    let mounted = true;
    void getCurrentSession().then((session) => {
      if (mounted) setStatus(session ? 'signedIn' : 'signedOut');
    });
    // Live updates: magic-link return, sign-out, token refresh.
    const unsubscribe = onAuthChange((session) => {
      setStatus(session ? 'signedIn' : 'signedOut');
    });
    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  if (status === 'loading') return <Splash />;
  if (status === 'signedOut') return <SignInScreen />;
  return <>{children}</>;
}

function Splash() {
  return (
    <div className="w-full h-screen flex items-center justify-center bg-cream">
      <div className="text-ink-3 text-sm">Loading your library…</div>
    </div>
  );
}

function SignInScreen() {
  const [email, setEmail] = useState('');
  const [phase, setPhase] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const submit = async () => {
    if (!emailValid || phase === 'sending') return;
    setPhase('sending');
    setErrorMsg('');
    try {
      await sendMagicLink(email.trim());
      setPhase('sent');
    } catch (e) {
      setPhase('error');
      setErrorMsg(e instanceof Error ? e.message : 'Could not send the sign-in link.');
    }
  };

  return (
    <div className="w-full h-screen flex items-center justify-center bg-cream px-6">
      <div
        className="w-full max-w-sm rounded-lg border border-cream-border bg-cream-2 p-8"
        style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}
      >
        <div className="mb-6">
          <h1 className="text-xl font-bold text-ink">Branchworks</h1>
          <p className="text-sm text-ink-2 mt-1">Sign in to your scenario library.</p>
        </div>

        {phase === 'sent' ? (
          <div className="space-y-4">
            <div className="rounded border border-cream-border bg-cream p-4 text-sm text-ink-2">
              Check your email — we sent a sign-in link to{' '}
              <span className="font-bold text-ink">{email.trim()}</span>. Click it to finish
              signing in. (Check spam if it doesn't arrive within a minute.)
            </div>
            <button
              onClick={() => {
                setPhase('idle');
                setEmail('');
              }}
              className="text-xs text-ink-2 hover:text-ink underline"
            >
              Use a different email
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-ink-2">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (phase === 'error') setPhase('idle');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void submit();
                }}
                placeholder="you@example.com"
                autoFocus
                className="w-full mt-1 px-3 py-2 rounded border border-cream-border bg-white text-sm"
              />
            </label>
            <button
              onClick={() => void submit()}
              disabled={!emailValid || phase === 'sending'}
              className="w-full px-4 py-2 rounded text-sm font-bold text-white disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: '#B8551F' }}
            >
              {phase === 'sending' ? 'Sending…' : 'Send magic link'}
            </button>
            {phase === 'error' && (
              <div className="text-xs text-terracotta-dark">{errorMsg}</div>
            )}
            <p className="text-[11px] text-ink-3 leading-relaxed">
              No password needed — we'll email you a one-time link. New here? The same link
              creates your account.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
